import React from 'react';
import {
  Stack,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from '@mui/material';
import { PageTitle } from 'components/shared/page_title';
import DocumentComponent from 'components/shared/document_component';
import { useLocation } from 'react-router-dom';

type Beneficiary = {
  cifa_uuid: string;
  nome: string;
  cpf: string;
  contato_telefonico: string;
  unidade_familiar: string;
  comunidade_localidade: string;
  extensionista: {
    nome: string;
    perfil: string;
    registro: string;
  };
  atendimentos: {
    cifa_uuid: string;
    tipo_atendimento: string;
    atividades: string[];
  }[];
};

interface ViewTechAssistProps {
  values: {
    beneficiaries: string;
    ataUuid: string | null;
    parceria: string;
    beneficiariosParceria: string;
    docParceriaUuid: string | null;
    relatorioUuid: string | null;
    instituicoes: string[];
    outraInstituicao: string;
    relatorioUuid_empaer: string | null;
    relatorioUuid_ensino: string | null;
    relatorioUuid_ongs: string | null;
    relatorioUuid_privadas: string | null;
    beneficiarios?: Beneficiary[];
  };
}

const INSTITUICOES_PARCEIRAS = [
  { label: 'EMPAER MT', value: 'empaer' },
  { label: 'Instituições de Ensino', value: 'ensino' },
  { label: 'Organizações – ONGs', value: 'ongs' },
  { label: 'Instituições privadas/outros', value: 'privadas' },
];

const ViewTechAssist: React.FC<ViewTechAssistProps> = ({ values }) => {
  const location = useLocation();
  const match = /cifa-(\d{4})/.exec(location.pathname);
  const anoReferencia = match ? Number(match[1]) : 2026;

  // Helper para exibir nome do arquivo (DocumentComponent deve exibir nome/link)
  const renderDocumento = (uuid: string | null, label: string) => (
    uuid ? (
      <Box mb={1}>
        <Typography fontWeight={500}>{label}:</Typography>
        <DocumentComponent uuid={uuid} podeEnviar={false} />
      </Box>
    ) : null
  );

  return (
    <>
      <PageTitle
        title={`Assistência Técnica e Extensão Rural - Município`}
        subtitle={`Visualização dos dados sobre os atendimentos ATER realizados no Município em ${anoReferencia}.`}
      />

      <Stack marginX="4rem" spacing="2rem" maxWidth="1152px" width="100%">
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Parcerias Institucionais
          </Typography>
          <Typography>
            O município realizou parceria institucional?{' '}
            <b>{values.parceria === 'Sim' ? 'Sim' : 'Não'}</b>
          </Typography>
          <Typography mt={2} fontWeight={500}>
            Instituições selecionadas:
          </Typography>
          <Typography>
            {values.instituicoes && values.instituicoes.length > 0
              ? values.instituicoes
                .map((inst) => {
                  const found = INSTITUICOES_PARCEIRAS.find(i => i.value === inst);
                  if (inst === 'privadas') return values.outraInstituicao || 'Instituição privada/outros';
                  return found ? found.label : inst;
                })
                .join(', ')
              : 'Nenhuma'}
          </Typography>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Quantidade de Beneficiários Atendidos
          </Typography>
          <Typography>
            <b>Diretamente pelo município:</b> {values.beneficiaries}
          </Typography>
          {values.parceria === 'Sim' && (
            <Typography>
              <b>Por instituições parceiras:</b> {values.beneficiariosParceria}
            </Typography>
          )}
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Lista de Beneficiários
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>CPF</TableCell>
                  <TableCell>Tipo de Atendimento</TableCell>
                  <TableCell>Atividades Realizadas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {values.beneficiarios && values.beneficiarios.length > 0 ? (
                  values.beneficiarios.map((beneficiary, index) =>
                    beneficiary.atendimentos.map((atendimento, idx) => (
                      <TableRow key={`${index}-${idx}`}>
                        {idx === 0 && (
                          <>
                            <TableCell rowSpan={beneficiary.atendimentos.length}>
                              {beneficiary.nome}
                            </TableCell>
                            <TableCell rowSpan={beneficiary.atendimentos.length}>
                              {beneficiary.cpf}
                            </TableCell>
                          </>
                        )}
                        <TableCell>{atendimento.tipo_atendimento}</TableCell>
                        <TableCell>{atendimento.atividades.join(', ')}</TableCell>
                      </TableRow>
                    ))
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>
                      Nenhum beneficiário adicionado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Documentos Enviados
          </Typography>
          {renderDocumento(values.ataUuid, 'Ata do CMDRS')}
          {renderDocumento(values.docParceriaUuid, 'Documento da Parceria')}
          {values.instituicoes && values.instituicoes.length > 0 && (
            <Box>
              <Typography fontWeight={500} mb={1}>
                Relatórios Técnicos Consolidados das Instituições Parceiras:
              </Typography>
              <Stack direction="column" spacing={2}>
                {INSTITUICOES_PARCEIRAS.map(inst =>
                  values.instituicoes.includes(inst.value) && values[`relatorioUuid_${inst.value}` as keyof typeof values] ? (
                    <Box key={inst.value}>
                      <Typography fontWeight={500}>{inst.label}</Typography>
                      <DocumentComponent
                        uuid={values[`relatorioUuid_${inst.value}` as keyof typeof values] as string}
                        podeEnviar={false}
                      />
                    </Box>
                  ) : null
                )}
              </Stack>
            </Box>
          )}
        </Paper>
      </Stack>
    </>
  );
};

export default ViewTechAssist;
